import { Controller } from "stimulus";

export default class extends Controller {
  static get targets() {
    return ["manualSelect", "presetSelect", "copySelect", "preview"];
  }

  connect() {
    this.update();
  }

  update() {
    if (!this.hasPreviewTarget) return;

    const rows = this.buildRows();
    this.previewTarget.innerHTML = this.renderPreview(rows);
  }

  buildRows() {
    const rows = new Map();
    const contactMap = this.contactMap;

    if (this.hasManualSelectTarget) {
      this.selectedValues(this.manualSelectTarget).forEach((contactId) => {
        this.addRow(rows, contactId, contactMap[contactId], []);
      });
    }

    if (this.hasPresetSelectTarget) {
      this.selectedValues(this.presetSelectTarget).forEach((presetId) => {
        const preset = this.presetMap[presetId];
        if (!preset) return;

        preset.memberships.forEach((membership) => {
          this.addRow(rows, membership.contactId, contactMap[membership.contactId], membership.tags || []);
        });
      });
    }

    if (this.hasCopySelectTarget) {
      const copyWebsite = this.websiteMap[this.copySelectTarget.value];
      if (copyWebsite) {
        copyWebsite.memberships.forEach((membership) => {
          this.addRow(rows, membership.contactId, contactMap[membership.contactId], membership.tags || []);
        });
      }
    }

    Object.keys(this.existingMap).forEach((contactId) => {
      if (!rows.has(String(contactId))) return;
      this.addRow(rows, contactId, contactMap[contactId], this.existingMap[contactId].tags || []);
    });

    return Array.from(rows.values());
  }

  addRow(rows, contactId, contact, tags) {
    if (!contact) return;

    const key = String(contactId);
    const current = rows.get(key) || { ...contact, tags: [] };
    current.tags = this.mergeTags(current.tags, tags);
    rows.set(key, current);
  }

  mergeTags(existingTags, incomingTags) {
    const combined = [...(existingTags || []), ...(incomingTags || [])].filter(Boolean);
    const unique = Array.from(new Set(combined));
    const known = this.tagList.filter((tag) => unique.includes(tag));
    const extra = unique.filter((tag) => !known.includes(tag));
    return [...known, ...extra];
  }

  renderPreview(rows) {
    const warningHtml = this.renderWarnings(rows);

    if (!rows.length) {
      return `<div class="ui segment"><h4>Contact Assignment Preview</h4><p>No contacts will be added.</p>${warningHtml}</div>`;
    }

    const items = rows.map((row) => {
      const email = row.email ? ` <small>(${this.escapeHtml(row.email)})</small>` : "";
      const tags = row.tags.length ? `<div><small>Tags: ${this.escapeHtml(row.tags.join(" / "))}</small></div>` : "";
      return `<li><strong>${this.escapeHtml(row.name)}</strong>${email}${tags}</li>`;
    });

    return `<div class="ui segment"><h4>Contact Assignment Preview</h4><ul class="ui list">${items.join("")}</ul>${warningHtml}</div>`;
  }

  renderWarnings(rows) {
    const missingUserRows = rows.filter((row) => !row.hasUser);
    if (!missingUserRows.length) return "";

    const names = missingUserRows.map((row) => this.escapeHtml(row.name)).join(", ");
    return `<div class="ui warning message"><div class="header">Contacts without linked login access</div><p>${names}</p></div>`;
  }

  selectedValues(selectTarget) {
    return Array.from(selectTarget.selectedOptions || []).map((option) => option.value).filter(Boolean);
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  get contactMap() {
    if (!this._contactMap) this._contactMap = this.parseData("contactMap");
    return this._contactMap;
  }

  get presetMap() {
    if (!this._presetMap) this._presetMap = this.parseData("presetMap");
    return this._presetMap;
  }

  get websiteMap() {
    if (!this._websiteMap) this._websiteMap = this.parseData("websiteMap");
    return this._websiteMap;
  }

  get existingMap() {
    if (!this._existingMap) this._existingMap = this.parseData("existingMap");
    return this._existingMap;
  }

  get tagList() {
    if (!this._tagList) this._tagList = JSON.parse(this.data.get("tagList") || "[]");
    return this._tagList;
  }

  parseData(name) {
    return JSON.parse(this.data.get(name) || "{}");
  }
}
